"use client";

import { useEffect, useRef } from "react";
import { trackSectionView } from "@/utils/analytics";

interface TrackedSectionProps {
  sectionId: string;
  sectionName: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Client component wrapper that tracks when a section comes into view
 */
export default function TrackedSection({
  sectionId,
  sectionName,
  children,
  className,
}: TrackedSectionProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const hasTracked = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasTracked.current) {
            hasTracked.current = true;
            trackSectionView({
              section_id: sectionId,
              section_name: sectionName,
            });
          }
        });
      },
      {
        threshold: 0.5, // Track when 50% of section is visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [sectionId, sectionName]);

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id={sectionId}
      className={className}
    >
      {children}
    </section>
  );
}
